// createInitialStore.js
'use strict';
import { createStore, applyMiddleware } from 'redux';
import { thunk } from 'redux-thunk';
import { fromJS } from 'immutable';
import XssFilters from 'xss-filters';

import { redux as reduxDebug } from './resource/debug.js';
import reducer, { createRootReducer } from './reducer/reducer.js';

const reduxLog = reduxDebug.extend('log');

// User will see NOTHING before this promise resolved.
const _createInitialStore = async ({
  query,
  extra: { headerCountry, language, ssrHeaderLanguage, initI18nLanguage },
  // used to init state.router so that getRouterData selectors work during SSR
  routerLocation,
}) => {
  const log = reduxLog.extend('_createInitialStore');
  log('init');

  /**
   * When a request URL is available, use createRootReducer with a static
   * location reducer so that getRouterData(state, 'location.search') returns
   * the correct value during SSR rendering (fixes URL query params like `sorts` not being applied on the first server render).
   */
  let rootReducer = reducer;
  if (routerLocation) {
    const routerInitialState = fromJS({ location: routerLocation });
    const staticRouterReducer = (state = routerInitialState) => state;
    rootReducer = createRootReducer(staticRouterReducer);
  }

  const defaultState = createStore(rootReducer).getState();

  let region = null;
  if (Array.isArray(query.region)) {
    region = query.region[0];
  } else {
    region = query.region;
  }

  const preloadedState = {
    me: { country: headerCountry || defaultState.getIn(['me', 'country']) },
    operations: {
      config: {
        language,
        initI18nLanguage, // To monitor i18n parse header result
        ssrHeaderLanguage, // To monitor CDN/Nginx behavior
        currentConfigKey: region,
      },
    },
  };
  log('preloadedState', preloadedState);

  const store = createStore(
    rootReducer,
    defaultState.mergeDeep(preloadedState),
    applyMiddleware(thunk)
  );

  // only add modal on SSR, to prevent add modal on init store at client side.
  if ('tw' === headerCountry) {
    const addModal = (await import('./action/addModal.js')).default;
    store.dispatch(addModal({ id: 'LandingPageModal', isLowerThanAll: true }));
  }

  log('end');
  return { store };
};

const filterObject = (object = {}) => {
  return Object.keys(object).reduce((current, key) => {
    const assignObject = {};
    assignObject[key] = XssFilters.inHTMLData(object[key]);
    return Object.assign({}, current, assignObject);
  }, {});
};

export const createInitialStore = ({
  params,
  query,
  cookies,
  extra,
  routerLocation,
} = {}) => {
  const safeParams = filterObject(params);
  const safeQuery = filterObject(query);
  const safeCookies = filterObject(cookies);
  const safeExtra = filterObject(extra);
  const safeRouterLocation = routerLocation
    ? filterObject(routerLocation)
    : undefined;
  return _createInitialStore({
    params: safeParams,
    query: safeQuery,
    cookies: safeCookies,
    extra: safeExtra,
    routerLocation: safeRouterLocation,
  });
};

export const createInitialStoreWithRequest = async ({
  params,
  request,
  extra = {},
} = {}) => {
  const log = reduxLog.extend('createInitialStoreWithRequest');
  const errorLog = reduxLog.extend('createInitialStoreWithRequest');
  log('init', { request, extra });

  try {
    const query = Object.fromEntries(
      new URLSearchParams(request.url.split('?')[1])
    );
    const initI18nLanguage = request.i18n?.language;
    const ssrHeaderLanguage = request.headers
      .get?.('Accept-Language')
      ?.toLowerCase?.();
    // Backend nginx gives `TW` as default, but we need it to be null.
    // const headerCountry = request.get?.('X-Client-Region')?.toLowerCase?.();

    log('processing language', {
      initI18nLanguage,
      ssrHeaderLanguage,
      query,
    });
    let language = initI18nLanguage;

    // when pwa, don't use lang query
    if (!query.pwa) {
      if (Array.isArray(query.lang)) {
        // Server will prepend lang query if lang is invalid or empty
        language = query.lang[0];
      } else {
        language = query.lang || language;
      }
    }
    request.language = language;
    if (request.i18n?.language !== language) {
      await request.i18n?.changeLanguage?.(language);
    }
    log('set language', language);

    const requestUrl = new URL(request.url);

    const createStoreArgument = {
      params,
      query,
      cookies: request.cookies,
      extra: {
        language,
        initI18nLanguage,
        ssrHeaderLanguage,
        ...extra,
      },
      routerLocation: {
        pathname: requestUrl.pathname,
        search: requestUrl.search,
      },
    };
    log('createStoreArgument', createStoreArgument);

    return createInitialStore(createStoreArgument);
  } catch (error) {
    errorLog('error in createInitialStoreWithRequest', error.toString());
    throw error;
  }
};

export default createInitialStore;
